import type { ComponentPropsWithoutRef } from 'react';

import type { CSSObject, MantineTheme } from '@mantine/core';
import { type Sx, useMantineTheme } from '@mantine/core';

import type { Pills } from '../pills';

type PillsProps = ComponentPropsWithoutRef<typeof Pills>;

type UsePillsVariantProps = Pick<
  PillsProps,
  'variant' | 'color' | 'customColor'
>;

interface UsePillsVariantReturn {
  sx: Sx;
}

export function usePillsVariant({
  variant,
  color,
  customColor,
}: UsePillsVariantProps): UsePillsVariantReturn {
  const theme = useMantineTheme();

  if (variant === 'light') {
    return { sx: getLightVariantSx(theme, color, customColor) };
  }

  return { sx: {} };
}

function getLightVariantSx(
  theme: MantineTheme,
  color: PillsProps['color'],
  customColor: PillsProps['customColor'],
): Sx {
  const commonSx: CSSObject = {
    borderWidth: 1,
    borderStyle: 'solid',
  };

  if (customColor) {
    return {
      ...commonSx,
      backgroundColor: customColor,
      borderColor: theme.fn.darken(customColor, 0.1),
      color: theme.colors.gray[9],
    };
  }

  if (color === 'blue') {
    return {
      ...commonSx,
      backgroundColor: theme.colors.blue[0],
      borderColor: theme.colors.blue[1],
      color: theme.colors.gray[9],
    };
  }

  if (color === 'red') {
    return {
      ...commonSx,
      backgroundColor: theme.colors.red[0],
      borderColor: theme.colors.red[1],
      color: theme.colors.gray[9],
    };
  }

  if (color === 'green') {
    return {
      ...commonSx,
      backgroundColor: theme.colors.teal[0],
      borderColor: theme.colors.teal[2],
      color: theme.colors.gray[9],
    };
  }

  if (color === 'orange') {
    return {
      ...commonSx,
      backgroundColor: theme.colors.orange[0],
      borderColor: theme.colors.orange[1],
      color: theme.colors.gray[9],
    };
  }

  if (color === 'yellow') {
    return {
      ...commonSx,
      backgroundColor: theme.colors.yellow[0],
      borderColor: theme.colors.yellow[1],
      color: theme.colors.gray[9],
    };
  }

  return {
    ...commonSx,
    backgroundColor: theme.colors.gray[1],
    borderColor: theme.colors.gray[2],
    color: theme.colors.gray[9],
  };
}
